// Address -> coordinates, for the location coordinates dialog only (it runs when
// someone presses "search", not while typing).
//
// Google Places when there is an API key, because it knows house numbers:
// OpenStreetMap has no address points on plenty of streets, and there the best it
// can answer is the road itself, whose point is the middle of the way — hundreds
// of metres from the doorway on a long street, and that point is what the guest is
// measured against. Without a key it falls back to Nominatim, so a dev environment
// with no key still works.
//
// Every result exposes resolve(): a suggestion from Places has no coordinates
// until its details are fetched, and that call is billed, so it happens for the
// one result the host picks and not for the five that are merely listed.
import { GoogleMapsLoader } from "./GoogleMapsLoader";

const NOMINATIM_SEARCH_URL = "https://nominatim.openstreetmap.org/search";

export const GeocodingHelper = {
    searchAddress,
    parseCoordinates,
    isValidCoordinate,
};

async function searchAddress(query, language = "en", limit = 5) {
    return GoogleMapsLoader.hasApiKey()
        ? searchAddressWithGoogle(query, language, limit)
        : searchAddressWithNominatim(query, language, limit);
}

async function searchAddressWithGoogle(query, language, limit) {
    const { AutocompleteSessionToken, AutocompleteSuggestion } =
        await GoogleMapsLoader.loadPlacesLibrary(language);

    // One token covers the typing and the pick, so Google bills the pair as a
    // single autocomplete session instead of two unrelated calls.
    const sessionToken = new AutocompleteSessionToken();

    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken,
    });

    return (suggestions || [])
        .map(suggestion => suggestion.placePrediction)
        .filter(Boolean)
        .slice(0, limit)
        .map(prediction => ({
            label: prediction.text?.text || prediction.mainText?.text || "",
            // 'route' is the whole street, same trap as OSM's road match: the
            // point is the middle of the way, not a doorway.
            isStreet: (prediction.types || []).includes("route"),
            resolve: async () => {
                const place = prediction.toPlace();
                await place.fetchFields({ fields: ["formattedAddress", "location"] });

                const latitude = place.location?.lat();
                const longitude = place.location?.lng();
                if (!isValidCoordinate(latitude, longitude))
                    throw new Error("Places returned a place with no usable location");

                return {
                    label: place.formattedAddress || prediction.text?.text || "",
                    latitude,
                    longitude,
                };
            },
        }))
        .filter(result => !!result.label);
}

async function searchAddressWithNominatim(query, language, limit) {
    const params = new URLSearchParams({
        q: query,
        format: "jsonv2",
        limit: String(limit),
        "accept-language": language,
    });

    const response = await fetch(`${NOMINATIM_SEARCH_URL}?${params.toString()}`, {
        headers: { Accept: "application/json" },
    });

    if (!response.ok)
        throw new Error(`Geocoding request failed: ${response.status}`);

    const results = await response.json();

    const found = (results || [])
        .map(result => {
            const latitude = Number(result.lat);
            const longitude = Number(result.lon);

            return {
                label: result.display_name,
                latitude,
                longitude,
                // A road match is the whole street, and its point is the centre of
                // the way: on a long street that is nowhere near the doorway.
                // Flagged so the caller can say so instead of letting it pass for
                // an address.
                isStreet: result.addresstype === "road" || result.category === "highway",
                // Nominatim already answered with the point; nothing left to ask.
                resolve: async () => ({ label: result.display_name, latitude, longitude }),
            };
        })
        .filter(result => isValidCoordinate(result.latitude, result.longitude));

    // OSM splits a street into as many ways as it has sections, and each one
    // comes back as its own hit with the very same label. Offering four rows
    // that read identically is worse than offering one: the point is refined on
    // the map anyway.
    const seen = new Set();
    return found.filter(result => {
        if (seen.has(result.label))
            return false;

        seen.add(result.label);
        return true;
    });
}

// "41.38879, 2.15899" — pasting a coordinate pair is a common way to get a point
// out of Google Maps, so accept it instead of sending it to the geocoder.
function parseCoordinates(text) {
    const parts = String(text).split(",").map(part => part.trim());
    // Both halves must be there: Number("") is 0, so "41.38879," would otherwise
    // pass as a valid point off the coast of Africa instead of being searched.
    if (parts.length !== 2 || parts.some(part => part === ""))
        return null;

    const latitude = Number(parts[0]);
    const longitude = Number(parts[1]);

    if (!isValidCoordinate(latitude, longitude))
        return null;

    return { latitude, longitude };
}

function isValidCoordinate(latitude, longitude) {
    return Number.isFinite(latitude)
        && Number.isFinite(longitude)
        && Math.abs(latitude) <= 90
        && Math.abs(longitude) <= 180;
}
